import React from 'react';
import { toNumber } from 'lodash-es';
import { useApiLoadingPool, usePreventInfiniteLoading, useRouter, useScrollToTop, } from '@/util/hook/custom';
import useCallback from '@/util/hook/useCallback';
import useDispatch from '@/util/hook/useDispatch';
import useMemo from '@/util/hook/useMemo';
import useSelector from '@/util/hook/useSelector';
import useState from '@/util/hook/useState';
import { selectLiffId } from '@/util/redux/auth';
import { getGenderOptionList } from '../../util/helper/option';
import { handleParamVerifyAndCachePut } from '../../util/helper/paramVerifyAndCachePut';
import { useGetCareClientVisitorProfile } from '../../util/hook/useGetCareClientVisitorProfile';
import { useGetRemoteConfig } from '../../util/hook/useGetRemoteConfig';
import { useScrollToInputField } from '../../util/hook/useScrollToInputField';
import { useTrackBrowserHistory } from '../../util/hook/useTrackBrowserHistory';
import { useValidateCareClientVisitorProfileAndRedirect } from '../../util/hook/useValidateCareClientVisitorProfileAndRedirect';
import { useValidateCareOrderBabyForm } from '../../util/hook/useValidateCareOrderBabyForm';
import { careOrderBabyFormUpdate, selectCareOrderBabyForm, } from '../../redux/module/careOrderBabyForm';
import View from './View';
import { APP } from '../../config/app';
import { POPUP_TYPE } from '../../config/popup';
import { CareOrderBabyFormForCachePutDConverter } from '../../util/dataConverter/CareOrderBabyFormForCachePutDConverter';
import { CareOrderBabyFormForParamVerifyDConverter } from '../../util/dataConverter/CareOrderBabyFormForParamVerifyDConverter';
import { CareOrderBabyFormUConverter } from '../../util/uiConverter/CareOrderBabyForm';
export default function Controller() {
    const { isLoading, setLoading, setApiProcessingPool, setApiDonePool } = useApiLoadingPool(true);
    const [popupType, setPopupType] = useState(POPUP_TYPE.CLOSE);
    const _id = useSelector(selectLiffId);
    const careOrderBabyForm = useSelector(selectCareOrderBabyForm);
    const { getIsFieldError, removeErrorType, setErrorTypes } = useValidateCareOrderBabyForm({
        gender: careOrderBabyForm.gender,
        height: careOrderBabyForm.height,
        weight: careOrderBabyForm.weight,
        age: careOrderBabyForm.age,
        hospitalBed: careOrderBabyForm.hospitalBed,
    });
    const { careOrderBabyFormInputFieldRefMap, scrollToInputField } = useScrollToInputField();
    const { navigate, navigateError } = useRouter();
    const dispatch = useDispatch();
    useTrackBrowserHistory();
    useGetRemoteConfig({ setApiProcessingPool, setApiDonePool });
    useGetCareClientVisitorProfile({ setApiProcessingPool, setApiDonePool });
    useValidateCareClientVisitorProfileAndRedirect({ redirectRoute: null });
    useScrollToTop();
    usePreventInfiniteLoading({ setApiProcessingPool, setApiDonePool });
    const genderOptionList = getGenderOptionList();
    const isGenderError = getIsFieldError('gender');
    const isHeightError = getIsFieldError('height');
    const isWeightError = getIsFieldError('weight');
    const isAgeError = getIsFieldError('age');
    const isHospitalBedError = getIsFieldError('hospitalBed');
    const isSymptomError = getIsFieldError('symptom');
    const isTubeError = getIsFieldError('tube');
    const careOrderBabyFormForPatientInfoPage = useMemo(() => {
        const data = CareOrderBabyFormUConverter.getDataForPatientInfoPage({ careOrderBabyForm });
        return data;
    }, [careOrderBabyForm]);
    // event handlers
    const handleGenderInputChange = useCallback((e) => {
        if (e.target.checked) {
            const gender = e.target.value;
            dispatch(careOrderBabyFormUpdate({
                gender,
            }));
        }
    }, []);
    const handleHeightInputChange = useCallback((e) => {
        const height = e.target.value.replace(/[^0-9]/g, '');
        dispatch(careOrderBabyFormUpdate({
            height: height ? toNumber(height) : null,
        }));
    }, []);
    const handleWeightInputChange = useCallback((e) => {
        const weight = e.target.value.replace(/[^0-9]/g, '');
        dispatch(careOrderBabyFormUpdate({
            weight: weight ? toNumber(weight) : null,
        }));
    }, []);
    const handleAgeInputChange = useCallback((e) => {
        const age = e.target.value.replace(/[^0-9]/g, '');
        dispatch(careOrderBabyFormUpdate({
            age: age ? toNumber(age) : null,
        }));
    }, []);
    const handleInputPopupClick = useCallback(({ errorType, popup }) => {
        removeErrorType(errorType);
        setPopupType(popup);
    }, []);
    const handleBackButtonClick = useCallback(() => {
        navigate(APP.ROUTES.HOSPITAL_CONFIRMATION);
    }, []);
    const handleForwardButtonClick = async () => {
        const { data: paramVerifyParams } = new CareOrderBabyFormForParamVerifyDConverter({
            gender: careOrderBabyForm.gender,
            height: careOrderBabyForm.height,
            weight: careOrderBabyForm.weight,
            age: careOrderBabyForm.age,
            hospitalBed: careOrderBabyForm.hospitalBed,
            symptom: careOrderBabyForm.symptom,
            tube: careOrderBabyForm.tube,
        });
        const { data: careOrderBabyFormForCachePut } = new CareOrderBabyFormForCachePutDConverter({
            careOrderBabyForm,
        });
        const { isSuccess } = await handleParamVerifyAndCachePut({
            _id,
            params: { _id, ...paramVerifyParams },
            careOrderBabyFormForCachePut,
            careOrderBabyFormInputFieldRefMap,
            pageRoute: APP.ROUTES.PATIENT_INFO,
            setLoading,
            setErrorTypes,
            scrollToInputField,
            navigateError,
        });
        if (isSuccess) {
            navigate(APP.ROUTES.CARE_REQUIREMENT);
        }
    };
    return (<View 
    // data
    isLoading={isLoading} genderOptionList={genderOptionList} gender={careOrderBabyForm.gender} height={careOrderBabyForm.height} weight={careOrderBabyForm.weight} age={careOrderBabyForm.age} hospitalBed={careOrderBabyForm.hospitalBed} isGenderError={isGenderError} isHeightError={isHeightError} isWeightError={isWeightError} isAgeError={isAgeError} isHospitalBedError={isHospitalBedError} isSymptomError={isSymptomError} isTubeError={isTubeError} careOrderBabyFormInputFieldRefMap={careOrderBabyFormInputFieldRefMap} popupType={popupType} careOrderBabyFormForPatientInfoPage={careOrderBabyFormForPatientInfoPage} 
    // func
    setPopupType={setPopupType} handleGenderInputChange={handleGenderInputChange} handleHeightInputChange={handleHeightInputChange} handleWeightInputChange={handleWeightInputChange} handleAgeInputChange={handleAgeInputChange} handleInputPopupClick={handleInputPopupClick} handleBackButtonClick={handleBackButtonClick} handleForwardButtonClick={handleForwardButtonClick}/>);
}
